import React, { useMemo, useState } from "react";
import axios from "axios";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Grid,
  Typography,
} from "@mui/material";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import SaveAltIcon from "@mui/icons-material/SaveAlt";
import ScienceIcon from "@mui/icons-material/Science";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import { downloadXlsx, loadWorkbook, sheetToObjects } from "../../utils/excelSheet";
import ConfirmDialog from "../../components/ConfirmDialog";
import { usePermissions } from "../../hooks/usePermissions";
import { CAPABILITIES } from "../../auth/permissions";

const formatMoney = (value) => {
  if (value === null || value === undefined || value === "") {
    return "";
  }

  return Number(value).toLocaleString("nl-NL", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const parseSaldo = (value) => {
  if (value === null || value === undefined || value === "") {
    return null;
  }

  if (typeof value === "number") {
    return Number.isFinite(value) ? value : null;
  }

  const parsed = Number(String(value).trim().replace(/\s/g, "").replace(",", "."));
  return Number.isFinite(parsed) ? parsed : null;
};

const readBankRows = async (file) => {
  const buffer = await file.arrayBuffer();
  const workbook = await loadWorkbook(buffer);
  const sheet = workbook.getWorksheet("Bank") || workbook.worksheets[0];
  const table = sheetToObjects(sheet, { defval: null });

  return table.map((row, index) => ({
    rowNumber: index + 2,
    customerNumber: row.Klantnummer == null ? "" : String(row.Klantnummer).trim(),
    dossierCode: row.Dossiercode == null ? "" : String(row.Dossiercode).trim(),
    saldo: parseSaldo(row.Saldo),
    excelYear: row.Jaar == null ? "" : String(row.Jaar).trim(),
    excelPeriod: row.Periode_1 == null ? "" : String(row.Periode_1).trim(),
    bookingPeriod: row.Periode == null ? "" : String(row.Periode).trim(),
    matchStatus: row["Afletter status"] == null ? "" : String(row["Afletter status"]),
  }));
};

const formatExcelPeriod = (row) => {
  const year = row.excelYear ? String(row.excelYear).padStart(2, "0") : "";
  const period = row.excelPeriod ? String(row.excelPeriod).padStart(4, "0") : "";
  if (year && period.length >= 4) {
    const fullYear = year.length === 2 ? `20${year}` : year;
    return `${fullYear} ${period.slice(0, 2)}-${period.slice(2, 4)}`;
  }

  return [row.bookingPeriod, year, period].filter(Boolean).join(" ");
};

const formatDossierPeriod = (row) => {
  if (!row.dossierYear || !row.dossierStartMonth || !row.dossierEndMonth) {
    return "";
  }

  const startMonth = String(row.dossierStartMonth).padStart(2, "0");
  const endMonth = String(row.dossierEndMonth).padStart(2, "0");
  return `${row.dossierYear} ${startMonth}-${endMonth}`;
};

const moneyOrEmpty = (value) => (value === null || value === undefined || value === "" ? "" : Number(value));

const SALDO_EXPORT_COLUMNS = [
  { header: "Row", width: 8, value: (item) => item.rowNumber },
  { header: "Excel customer", width: 16, value: (item) => item.customerNumber || "" },
  { header: "DB customer", width: 16, value: (item) => item.entityNumber || "" },
  { header: "Customer name", width: 32, value: (item) => item.entityName || "" },
  { header: "Dossier code", width: 22, value: (item) => item.dossierCode || "" },
  { header: "Excel period", width: 16, value: (item) => formatExcelPeriod(item) },
  { header: "Dossier period", width: 16, value: (item) => formatDossierPeriod(item) },
  { header: "Excel status", width: 16, value: (item) => item.matchStatus || "" },
  { header: "Action", width: 16, value: (item) => item.action || "" },
  { header: "Skip reason", width: 56, value: (item) => item.skipReason || "" },
  { header: "Current refund", width: 16, value: (item) => moneyOrEmpty(item.currentRefundAmount) },
  { header: "Current payout", width: 16, value: (item) => moneyOrEmpty(item.currentPayoutAmount) },
  { header: "Excel saldo", width: 14, value: (item) => moneyOrEmpty(item.excelSaldo) },
  { header: "Difference", width: 14, value: (item) => moneyOrEmpty(item.payoutDifference) },
  { header: "New refund", width: 14, value: (item) => moneyOrEmpty(item.newRefundAmount) },
  { header: "New payout", width: 14, value: (item) => moneyOrEmpty(item.newPayoutAmount) },
  { header: "Paid", width: 10, value: (item) => item.payedOut ?? "" },
];

const downloadSaldoGridXlsx = async (report) => {
  const rows = (report.items || []).map((item) =>
    SALDO_EXPORT_COLUMNS.reduce((row, column) => {
      row[column.header] = column.value(item);
      return row;
    }, {})
  );
  const fileName = report.dryRun ? "prefinance-saldo-dryrun.xlsx" : "prefinance-saldo-report.xlsx";
  await downloadXlsx(
    rows,
    "Saldo comparison",
    fileName,
    SALDO_EXPORT_COLUMNS.map((column) => column.width)
  );
};

const SummaryCard = ({ label, value, color }) => (
  <Card variant="outlined">
    <CardContent>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="h5" color={color || "text.primary"}>
        {value ?? 0}
      </Typography>
    </CardContent>
  </Card>
);

const PrefinanceBankSaldoImport = () => {
  const { hasPermission } = usePermissions();
  const canImport = hasPermission(CAPABILITIES.adminMigrateLegacy, "X");
  const [fileName, setFileName] = useState("");
  const [rows, setRows] = useState([]);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [confirmDialog, setConfirmDialog] = useState({
    isOpen: false,
    title: "",
    subTitle: "",
    onConfirm: () => {},
  });

  const columns = useMemo(
    () => [
      { field: "rowNumber", headerName: "Row", width: 70 },
      { field: "customerNumber", headerName: "Excel customer", width: 130 },
      { field: "entityNumber", headerName: "DB customer", width: 120 },
      { field: "dossierCode", headerName: "Dossier code", width: 190 },
      {
        field: "excelPeriod",
        headerName: "Excel period",
        width: 130,
        valueGetter: (params) => formatExcelPeriod(params.row),
      },
      {
        field: "dossierYear",
        headerName: "Dossier period",
        width: 140,
        valueGetter: (params) => formatDossierPeriod(params.row),
      },
      { field: "matchStatus", headerName: "Excel status", width: 120 },
      { field: "action", headerName: "Action", width: 130 },
      {
        field: "skipReason",
        headerName: "Skip reason",
        flex: 1,
        minWidth: 280,
        cellClassName: "skip-reason-cell",
        renderCell: (params) => (
          <Box
            component="span"
            sx={{
              whiteSpace: "normal",
              overflowWrap: "anywhere",
              lineHeight: 1.45,
              py: 0.75,
            }}
          >
            {params.value}
          </Box>
        ),
      },
      {
        field: "currentRefundAmount",
        headerName: "Current refund",
        width: 130,
        valueFormatter: (params) => formatMoney(params.value),
      },
      {
        field: "currentPayoutAmount",
        headerName: "Current payout",
        width: 130,
        valueFormatter: (params) => formatMoney(params.value),
      },
      {
        field: "excelSaldo",
        headerName: "Excel saldo",
        width: 120,
        valueFormatter: (params) => formatMoney(params.value),
      },
      {
        field: "payoutDifference",
        headerName: "Difference",
        width: 110,
        valueFormatter: (params) => formatMoney(params.value),
      },
      {
        field: "newRefundAmount",
        headerName: "New refund",
        width: 120,
        valueFormatter: (params) => formatMoney(params.value),
      },
      {
        field: "newPayoutAmount",
        headerName: "New payout",
        width: 120,
        valueFormatter: (params) => formatMoney(params.value),
      },
      { field: "payedOut", headerName: "Paid", width: 70 },
    ],
    []
  );

  const handleChooseFile = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) {
      return;
    }

    setError("");
    setSuccess("");
    setReport(null);

    try {
      const parsedRows = await readBankRows(file);
      setFileName(file.name);
      setRows(parsedRows);
      setSuccess(`${parsedRows.length} rows read from ${file.name}. Run a dry-run before applying anything.`);
    } catch (readError) {
      console.error("Could not read the saldo workbook:", readError);
      setFileName("");
      setRows([]);
      setError("The Excel file could not be read. Use the Bank sheet with Klantnummer, Dossiercode and Saldo.");
    }
  };

  const handleCompare = async (dryRun) => {
    if (!canImport || rows.length === 0) {
      return;
    }

    setLoading(true);
    setError("");
    setSuccess("");

    try {
      const response = await axios.post(
        "/api/v1/preFinance/applyBankSaldos",
        { rows },
        { params: { dryRun }, timeout: 0 }
      );
      setReport(response.data);
      setSuccess(
        dryRun
          ? "Dry-run completed. No records were changed."
          : "Saldo import completed. Refund was filled from the previous payout, and payout was set to the Excel saldo."
      );
    } catch (requestError) {
      console.error("Prefinance saldo import failed:", requestError);
      setError(
        requestError.response?.data?.message ||
          requestError.response?.data ||
          "The saldo comparison could not be completed."
      );
    } finally {
      setLoading(false);
    }
  };

  const handleExportGrid = async () => {
    if (!report?.items?.length) {
      return;
    }

    await downloadSaldoGridXlsx(report);
  };

  const handleExecute = () => {
    setConfirmDialog({
      isOpen: true,
      title: "Apply bank saldos",
      subTitle:
        "This updates dossiers whose refund is still empty. Refund becomes the current payout, and payout becomes the Excel saldo. Nothing is recalculated. Continue?",
      onConfirm: () => {
        setConfirmDialog((currentDialog) => ({ ...currentDialog, isOpen: false }));
        handleCompare(false);
      },
    });
  };

  if (!canImport) {
    return (
      <Alert severity="warning">The admin.migrateLegacy permission is required to compare bank saldos.</Alert>
    );
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Prefinance saldo
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Read the bank review workbook and compare each dossier code with DWS. For a dossier that has no refund yet,
        refund is filled with the current payout and payout is replaced by the Excel Saldo column. No VF calculation
        is applied. Start with a dry-run. Dossier codes that appear more than once with different saldos are skipped.
      </Typography>

      <Box display="flex" gap={1} flexWrap="wrap" mb={2} alignItems="center">
        <Button
          variant="outlined"
          component="label"
          startIcon={<UploadFileIcon />}
          disabled={loading}
          aria-label="Choose bank saldo Excel file"
        >
          Choose Excel
          <input
            hidden
            type="file"
            accept=".xlsx,.xls"
            onChange={handleChooseFile}
          />
        </Button>
        <Button
          variant="contained"
          startIcon={loading ? <CircularProgress size={18} color="inherit" /> : <ScienceIcon />}
          onClick={() => handleCompare(true)}
          disabled={loading || rows.length === 0}
          aria-label="Run bank saldo dry-run"
        >
          Run dry-run
        </Button>
        <Button
          variant="contained"
          color="error"
          startIcon={<PlayArrowIcon />}
          onClick={handleExecute}
          disabled={loading || !report?.dryRun}
          aria-label="Apply bank saldos"
        >
          Apply saldos
        </Button>
        <Button
          variant="outlined"
          startIcon={<SaveAltIcon />}
          onClick={handleExportGrid}
          disabled={loading || !report?.items?.length}
          aria-label="Export saldo comparison grid to Excel"
        >
          Export Excel
        </Button>
        {fileName && (
          <Typography variant="body2" color="text.secondary">
            {fileName}: {rows.length} rows
          </Typography>
        )}
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
          {typeof error === "string" ? error : "The saldo comparison could not be completed."}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess("")}>
          {success}
        </Alert>
      )}

      {report && (
        <>
          <Box display="flex" alignItems="center" gap={1} mb={2}>
            <Chip
              label={report.dryRun ? "Dry-run" : "Applied"}
              color={report.dryRun ? "info" : "success"}
              size="small"
            />
            <Typography variant="body2" color="text.secondary">
              {report.items?.length || 0} Excel rows compared
            </Typography>
          </Box>

          <Grid container spacing={2} mb={2}>
            <Grid item xs={6} sm={4} md={2}>
              <SummaryCard label="Excel rows" value={report.excelRowCount} />
            </Grid>
            <Grid item xs={6} sm={4} md={2}>
              <SummaryCard label="To update" value={report.updateCount} color="success.main" />
            </Grid>
            <Grid item xs={6} sm={4} md={2}>
              <SummaryCard label="Refund already set" value={report.alreadyHasRefundCount} />
            </Grid>
            <Grid item xs={6} sm={4} md={2}>
              <SummaryCard label="Not found" value={report.notFoundCount} color="warning.main" />
            </Grid>
            <Grid item xs={6} sm={4} md={2}>
              <SummaryCard label="Skipped" value={report.skippedCount} color="warning.main" />
            </Grid>
          </Grid>

          <Card>
            <CardContent>
              <DataGrid
                autoHeight
                getRowHeight={() => "auto"}
                getRowId={(row) => `${row.rowNumber}-${row.dossierCode || "row"}`}
                rows={report.items || []}
                columns={columns}
                density="compact"
                disableSelectionOnClick
                pageSize={25}
                rowsPerPageOptions={[25, 50, 100]}
                components={{ Toolbar: GridToolbar }}
                componentsProps={{
                  toolbar: {
                    showQuickFilter: true,
                    quickFilterProps: { debounceMs: 250 },
                    csvOptions: { disableToolbarButton: true },
                    printOptions: { disableToolbarButton: true },
                  },
                }}
                sx={{
                  border: 0,
                  "& .MuiDataGrid-cell:hover": {
                    color: "primary.main",
                  },
                  "& .MuiDataGrid-cell.skip-reason-cell": {
                    whiteSpace: "normal",
                    alignItems: "flex-start",
                    lineHeight: 1.45,
                  },
                }}
              />
            </CardContent>
          </Card>
        </>
      )}

      <ConfirmDialog confirmDialog={confirmDialog} setConfirmDialog={setConfirmDialog} />
    </Box>
  );
};

export default PrefinanceBankSaldoImport;
